import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Search, 
  CreditCard,
  Banknote,
  Receipt,
  User,
  Package,
  Calculator,
  X
} from 'lucide-react';
import { SwipeGestures } from './SwipeGestures';
import { formatQuantity } from '@/lib/number-formatting';
import { parseQuantityInput } from '@/lib/quantity-utils';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  category: string;
  dte_type?: string; // 'B' para Bien, 'S' para Servicio
}

interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  dte_type?: string; // 'B' para Bien, 'S' para Servicio
  image?: string;
  stock: number;
}

// Productos de ejemplo
const SAMPLE_PRODUCTS: Product[] = [
  { id: '1', name: 'Coca Cola 600ml', price: 2.50, category: 'Bebidas', dte_type: 'B', stock: 45 },
  { id: '2', name: 'Pan Integral', price: 1.75, category: 'Panadería', dte_type: 'B', stock: 23 },
  { id: '3', name: 'Leche Entera 1L', price: 3.25, category: 'Lácteos', dte_type: 'B', stock: 15 },
  { id: '4', name: 'Arroz 1kg', price: 4.50, category: 'Granos', dte_type: 'B', stock: 12 },
  { id: '5', name: 'Aceite Vegetal', price: 5.75, category: 'Aceites', dte_type: 'B', stock: 8 },
  { id: '6', name: 'Pollo Entero', price: 12.50, category: 'Carnes', dte_type: 'B', stock: 6 }
];

const CATEGORIES = ['Todos', 'Bebidas', 'Panadería', 'Lácteos', 'Granos', 'Aceites', 'Carnes'];

export function MobilePOSView() {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);

  const filteredProducts = SAMPLE_PRODUCTS.filter(product => {
    const matchesCategory = selectedCategory === 'Todos' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1.0000,
        category: product.category,
        dte_type: product.dte_type || 'B' // Incluir tipo DTE
      }];
    });
  };

  const updateQuantity = (id: string, change: number) => {
    setCart(prev => 
      prev.map(item => {
        if (item.id === id) {
          const newQuantity = Math.max(0.0001, item.quantity + change);
          return newQuantity <= 0.0001 ? null : { ...item, quantity: parseQuantityInput(newQuantity.toString()) };
        }
        return item;
      }).filter(Boolean) as CartItem[]
    );
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  if (showCheckout) {
    return <MobileCheckout 
      cart={cart} 
      total={getTotalAmount()}
      onBack={() => setShowCheckout(false)}
      onComplete={() => {
        setCart([]);
        setShowCheckout(false);
        // Aquí iría la lógica de completar venta
      }}
    />;
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold">Punto de Venta</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCart(!showCart)}
            className="relative"
          >
            <ShoppingCart className="h-4 w-4" />
            {getTotalItems() > 0 && (
              <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 text-xs">
                {getTotalItems()}
              </Badge>
            )}
          </Button>
        </div>

        {/* Búsqueda */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar productos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Categorías */}
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {CATEGORIES.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="whitespace-nowrap flex-shrink-0"
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Productos Grid */}
      <div className="flex-1 p-4">
        <div className="grid grid-cols-2 gap-3">
          {filteredProducts.map(product => (
            <SwipeGestures
              key={product.id}
              onSwipeLeft={() => addToCart(product)}
              className="h-full"
            >
              <Card 
                className="apple-card h-full cursor-pointer active:scale-95 transition-transform"
                onClick={() => addToCart(product)}
              >
                <CardContent className="p-3">
                  <div className="aspect-square bg-muted rounded-lg mb-2 flex items-center justify-center">
                    <Package className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-medium text-sm leading-tight mb-1">{product.name}</h3>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-muted-foreground">{product.category}</p>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs ${
                        product.dte_type === 'S' ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                      }`}
                    >
                      {product.dte_type === 'S' ? 'Servicio' : 'Bien'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm">${product.price.toFixed(2)}</span>
                    <Badge variant="outline" className="text-xs">
                      {product.stock}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </SwipeGestures>
          ))}
        </div>
      </div>

      {/* Cart Overlay */}
      {showCart && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-md z-20 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-lg font-bold">Carrito ({getTotalItems()})</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowCart(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex-1 overflow-auto p-4">
            {cart.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>El carrito está vacío</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map(item => (
                  <Card key={item.id} className="apple-card">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{item.name}</h4>
                          <p className="text-xs text-muted-foreground">${item.price.toFixed(2)} c/u</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.id, -1)}
                            className="h-8 w-8 p-0"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="font-bold min-w-[2rem] text-center">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.id, 1)}
                            className="h-8 w-8 p-0"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{item.category}</span>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              item.dte_type === 'S' ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                            }`}
                          >
                            {item.dte_type === 'S' ? 'Servicio' : 'Bien'}
                          </Badge>
                        </div>
                        <span className="font-bold">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {cart.length > 0 && (
            <div className="p-4 border-t border-border bg-background">
              <div className="flex items-center justify-between mb-4">
                <span className="text-lg font-bold">Total:</span>
                <span className="text-xl font-bold text-primary">${getTotalAmount().toFixed(2)}</span>
              </div>
              <Button 
                className="w-full" 
                size="lg"
                onClick={() => setShowCheckout(true)}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Procesar Venta
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Componente de Checkout
function MobileCheckout({ 
  cart, 
  total, 
  onBack, 
  onComplete 
}: { 
  cart: CartItem[]; 
  total: number; 
  onBack: () => void; 
  onComplete: () => void; 
}) {
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card'>('cash');
  const [receivedAmount, setReceivedAmount] = useState('');

  const change = paymentMethod === 'cash' ? Math.max(0, parseFloat(receivedAmount || '0') - total) : 0;

  return (
    <div className="flex flex-col h-full bg-background">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-md border-b border-border p-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={onBack}>
            ← Volver
          </Button>
          <h1 className="text-xl font-bold">Procesar Venta</h1>
        </div>
      </div>

      <div className="flex-1 p-4 space-y-6">
        {/* Resumen */}
        <Card className="apple-card">
          <CardHeader>
            <CardTitle className="text-lg">Resumen de Venta</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.name}</span>
                  <span>${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className="border-t pt-2 flex justify-between font-bold">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Método de Pago */}
        <Card className="apple-card">
          <CardHeader>
            <CardTitle className="text-lg">Método de Pago</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={paymentMethod === 'cash' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('cash')}
                className="h-16 flex-col"
              >
                <Banknote className="h-6 w-6 mb-1" />
                Efectivo
              </Button>
              <Button
                variant={paymentMethod === 'card' ? 'default' : 'outline'}
                onClick={() => setPaymentMethod('card')}
                className="h-16 flex-col"
              >
                <CreditCard className="h-6 w-6 mb-1" />
                Tarjeta
              </Button>
            </div>

            {paymentMethod === 'cash' && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Monto Recibido</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={receivedAmount}
                    onChange={(e) => setReceivedAmount(e.target.value)}
                    className="text-lg text-center"
                  />
                </div>
                {change > 0 && (
                  <div className="bg-muted p-3 rounded-lg">
                    <div className="flex justify-between">
                      <span>Cambio:</span>
                      <span className="font-bold text-lg">${change.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="p-4 border-t border-border bg-background">
        <Button
          className="w-full"
          size="lg"
          onClick={onComplete}
          disabled={paymentMethod === 'cash' && parseFloat(receivedAmount || '0') < total}
        >
          <Receipt className="h-4 w-4 mr-2" />
          Completar Venta
        </Button>
      </div>
    </div>
  );
}